import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Wand2, Layers, Flame } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { track } from "../lib/track";

const PUBLIC_DOMAIN = "https://moodcard.app";

// iter-93 — Remix is now the user's chance to re-render the SAME feeling
// in a different voice/aesthetic. They never re-type the text. They tune
// the dials and ask the model to surprise them again.

const PACK_OPTIONS = [
    { id: "cinematic",     label: "Cinematic",  blurb: "atmospheric · film still" },
    { id: "minimal",       label: "Minimal",    blurb: "quiet · gallery white" },
    { id: "bold_gradient", label: "Gradient",   blurb: "loud · color-soaked" },
    { id: "faith",         label: "Devotional", blurb: "sacred · scripture-aware" },
    { id: "romantic",      label: "Romantic",   blurb: "soft · velvet-night" },
];

const SPICE_OPTIONS = [
    { id: "mild",     label: "Mild",     blurb: "honest, but kept gentle" },
    { id: "bold",     label: "Bold",     blurb: "the house default" },
    { id: "chaotic",  label: "Chaotic",  blurb: "group-chat energy" },
    { id: "unhinged", label: "Unhinged", blurb: "maximum online" },
];

export default function Remix() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [origin, setOrigin] = useState(null);
    const [pack, setPack] = useState(null);
    const [spice, setSpice] = useState("bold");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/c/${slug}`);
                if (!cancelled) {
                    setOrigin(data);
                    setPack(data?.pack || "cinematic");
                    track("remix_landing_viewed", { slug });
                }
            } catch {
                if (!cancelled) setError("couldn't find that card.");
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    const handleSubmit = async () => {
        if (!origin || submitting) return;
        setSubmitting(true);
        setError(null);
        track("share_landing_mood_submitted", { slug, source: "remix", pack, spice });
        try {
            // iter-93 — call the new remix endpoint with the parent's
            // mood_id. Backend reuses the feeling text + applies our chosen
            // pack/spice/voice combo and creates a child card linked via
            // parent_mood_id.
            const { data } = await axios.post(
                `${API}/moodcards/${origin.id}/remix`,
                { pack, spice },
                { withCredentials: true },
            );
            navigate(`/results/${data.id}`, { state: { mood: data } });
        } catch (e) {
            const detail = e?.response?.data?.detail || {};
            if (detail.error === "daily_quota_exhausted") {
                setError(detail.headline + " " + (detail.advice || ""));
            } else if (detail.error === "premium_pack_required") {
                setError("that pack is for Keeper. pick a free one for now.");
            } else {
                setError("couldn't remix this one. try again?");
            }
            setSubmitting(false);
        }
    };

    const remixUrl = `${PUBLIC_DOMAIN}/remix/${slug}`;

    const previewFeeling = origin?.feeling || "";
    const truncatedFeeling = previewFeeling.length > 140
        ? previewFeeling.slice(0, 140) + "…"
        : previewFeeling;

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="remix-screen">
            <Helmet>
                <title>Remix this mood — MoodCard</title>
                <link rel="canonical" href={remixUrl} />
                <meta name="description" content="Re-render this feeling in a new voice. Same words, your aesthetic. No signup." />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="MoodCard" />
                <meta property="og:title" content="Remix this mood — MoodCard" />
                <meta property="og:description" content="Same feeling, your aesthetic." />
                <meta property="og:url" content={remixUrl} />
            </Helmet>

            <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <header className="relative z-10 max-w-md mx-auto px-6 pt-6 flex items-center justify-between">
                <button
                    onClick={() => navigate(`/c/${slug}`)}
                    className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 hover:text-app transition-colors"
                    data-testid="remix-back-link"
                >
                    ← see original
                </button>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">remix</span>
            </header>

            <main className="relative z-10 max-w-md mx-auto px-6 pt-6 pb-32 flex flex-col">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-4">
                        same feeling · your voice
                    </p>
                    <h1
                        className="font-serif font-light italic text-app text-3xl sm:text-4xl leading-[1.05] tracking-tight"
                        data-testid="remix-headline"
                    >
                        re-render this in your aesthetic.
                    </h1>
                </motion.div>

                {/* Original feeling — read-only quote block */}
                {origin && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-6 rounded-2xl border border-app/20 bg-surface-1 backdrop-blur-xl p-5"
                        data-testid="remix-origin-block"
                    >
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 mb-2">
                            the original feeling
                        </p>
                        <p
                            className="font-serif italic text-app text-lg leading-snug"
                            data-testid="remix-original-feeling"
                        >
                            "{truncatedFeeling}"
                        </p>
                    </motion.div>
                )}

                {/* Pack picker — choose the aesthetic */}
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="mt-7"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Layers className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-2">
                            aesthetic
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {PACK_OPTIONS.map((opt) => {
                            const active = pack === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    onClick={() => setPack(opt.id)}
                                    data-testid={`remix-pack-${opt.id}`}
                                    className={[
                                        "text-left rounded-xl border px-3.5 py-3 transition-all",
                                        active
                                            ? "border-app/55 bg-surface-2 shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
                                            : "border-app/15 bg-surface-1/60 hover:border-app/30",
                                    ].join(" ")}
                                >
                                    <p className="font-sans text-[12px] font-medium text-app">{opt.label}</p>
                                    <p className="font-sans text-[10px] text-app-3 mt-0.5">{opt.blurb}</p>
                                </button>
                            );
                        })}
                    </div>
                </motion.section>

                {/* Spice picker — choose the emotional risk register */}
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="mt-6"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-2">
                            spice
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {SPICE_OPTIONS.map((opt) => {
                            const active = spice === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    onClick={() => setSpice(opt.id)}
                                    data-testid={`remix-spice-${opt.id}`}
                                    className={[
                                        "px-3.5 py-1.5 rounded-full border font-sans text-[11px] uppercase tracking-wide transition-all",
                                        active
                                            ? "border-app/55 bg-cta text-on-app"
                                            : "border-app/20 bg-surface-1/40 text-app-2 hover:border-app/40",
                                    ].join(" ")}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-2 font-sans italic text-[11px] text-app-3">
                        {SPICE_OPTIONS.find((o) => o.id === spice)?.blurb}
                    </p>
                </motion.section>

                {/* Error or quota message */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-5 rounded-xl border border-rose-300/30 bg-rose-300/5 px-4 py-3"
                        data-testid="remix-error"
                    >
                        <p className="font-serif italic text-[13px] text-rose-200/90 leading-snug">
                            {error}
                        </p>
                    </motion.div>
                )}

                <motion.button
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.45 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={!origin || submitting}
                    data-testid="remix-make-button"
                    className="mt-7 w-full group relative inline-flex items-center justify-center gap-3 py-4 px-6 rounded-full bg-cta text-on-app font-sans font-medium text-sm tracking-wide disabled:opacity-30 transition-all"
                >
                    {submitting ? <Wand2 className="w-4 h-4 animate-pulse" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                    <span>{submitting ? "Re-rendering…" : "Spin My Version"}</span>
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </motion.button>
                <p className="mt-4 text-center font-sans text-[11px] tracking-wide text-app-3">
                    same words. your aesthetic. no signup.
                </p>
            </main>
        </div>
    );
}
